import {
  GRANULARITY_DAY,
  GRANULARITY_MONTH,
  GRANULARITY_WEEK,
  MODE_BY_DATE,
  MODE_TIMELINE,
  RU_MONTH_GENITIVE,
  RU_MONTH_NOMINATIVE,
  RU_MONTH_SHORT,
  SERIES_COLORS,
} from './constants';

const DAY_MS = 86400000;
const WEEK_MS = 7 * DAY_MS;

// The backend remaps the two "inside a year" modes onto this reference year.
// 2024-01-01 is a Monday belonging to ISO week 1, so week N sits exactly
// 7 * (N - 1) days later and even week 53 has a slot.
const REF_WEEK_1 = Date.UTC(2024, 0, 1);

const ALL_YEARS_SUFFIX = ' · все годы';

/**
 * Axis dates arrive as plain 'YYYY-MM-DD'. They MUST be parsed as UTC midnight
 * and rendered with getUTC*, because ApexCharts formats a datetime axis in UTC
 * (datetimeUTC defaults to true). Parsing them as local midnight instead shifts
 * every label one day back for anyone east of Greenwich.
 */
export function parseAxis(axis) {
  return (axis || []).map((value) => Date.parse(`${value}T00:00:00Z`));
}

const pad2 = (value) => String(value).padStart(2, '0');

/** ISO week number of a real date. */
export function isoWeek(timestamp) {
  const date = new Date(timestamp);
  const weekday = (date.getUTCDay() + 6) % 7; // Monday = 0
  const thursday = timestamp + (3 - weekday) * DAY_MS;
  const january1 = Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1);
  return 1 + Math.floor((thursday - january1) / WEEK_MS);
}

/**
 * Week number of a date that lives on the reference-year axis.
 * Not the same as isoWeek: week 53 maps to 2024-12-30, which real ISO rules
 * would call week 1 of 2025.
 */
export function isoWeekOfRef(timestamp) {
  return 1 + Math.round((timestamp - REF_WEEK_1) / WEEK_MS);
}

function weekRangeLabel(timestamp) {
  const start = new Date(timestamp);
  const end = new Date(timestamp + 6 * DAY_MS);
  const startDay = pad2(start.getUTCDate());
  const endDay = pad2(end.getUTCDate());
  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${startDay}–${endDay} ${RU_MONTH_SHORT[start.getUTCMonth()]}`;
  }
  return `${startDay} ${RU_MONTH_SHORT[start.getUTCMonth()]} – ${endDay} ${
    RU_MONTH_SHORT[end.getUTCMonth()]
  }`;
}

/** Short label under a tick. */
export function axisFormatter(mode, granularity) {
  return (value) => {
    const timestamp = Number(value);
    if (Number.isNaN(timestamp)) return '';
    const date = new Date(timestamp);
    if (mode === MODE_TIMELINE) {
      if (granularity === GRANULARITY_MONTH) {
        return `${RU_MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
      }
      return `${pad2(date.getUTCDate())}.${pad2(
        date.getUTCMonth() + 1,
      )}.${String(date.getUTCFullYear()).slice(2)}`;
    }
    if (granularity === GRANULARITY_MONTH) {
      return RU_MONTH_NOMINATIVE[date.getUTCMonth()];
    }
    return `${pad2(date.getUTCDate())} ${RU_MONTH_SHORT[date.getUTCMonth()]}`;
  };
}

/** Header line of the tooltip. */
export function tooltipFormatter(mode, granularity) {
  return (value) => {
    const timestamp = Number(value);
    if (Number.isNaN(timestamp)) return '';
    const date = new Date(timestamp);
    const suffix = mode === MODE_BY_DATE ? ALL_YEARS_SUFFIX : '';

    if (granularity === GRANULARITY_WEEK) {
      const week = mode === MODE_TIMELINE ? isoWeek(timestamp) : isoWeekOfRef(timestamp);
      const year = mode === MODE_TIMELINE ? ` ${date.getUTCFullYear()}` : '';
      return `Неделя ${weekRangeLabel(timestamp)}${year} (нед. ${week})${suffix}`;
    }
    if (granularity === GRANULARITY_MONTH) {
      const month = RU_MONTH_NOMINATIVE[date.getUTCMonth()];
      if (mode === MODE_TIMELINE) return `${month} ${date.getUTCFullYear()}`;
      return `${month}${suffix}`;
    }
    const day = `${date.getUTCDate()} ${RU_MONTH_GENITIVE[date.getUTCMonth()]}`;
    if (mode === MODE_TIMELINE) return `${day} ${date.getUTCFullYear()} г.`;
    return `${day}${suffix}`;
  };
}

/**
 * Payload -> ApexCharts series. The x array is identical for every series
 * because the backend sends a dense axis; that is what makes the shared
 * tooltip line up, since ApexCharts pairs series by index.
 */
export function buildSeries(payload, cumulative) {
  if (!payload) return [];
  const axis = parseAxis(payload.axis);
  return (payload.series || []).map((item) => {
    let running = 0;
    return {
      name: item.name,
      data: (item.data || []).map((value, index) => {
        if (value === null || value === undefined) {
          return { x: axis[index], y: null };
        }
        running += value;
        return { x: axis[index], y: cumulative ? running : value };
      }),
    };
  });
}

/** The single highest point across all series - the "peak" tile. */
export function findPeak(payload) {
  if (!payload || !payload.series || !payload.series.length) return null;
  let best = null;
  payload.series.forEach((item) => {
    (item.data || []).forEach((value, index) => {
      if (value === null || value === undefined) return;
      if (best === null || value > best.value) {
        best = { value, index, seriesName: item.name };
      }
    });
  });
  if (!best) return null;
  const axis = parseAxis(payload.axis);
  return {
    ...best,
    label: tooltipFormatter(payload.mode, payload.granularity)(axis[best.index]),
  };
}

export function buildChartOptions({ mode, granularity, cumulative }) {
  // "Общий по дате" по месяцам is a comparison of twelve magnitudes rather than
  // a time series, so bars read better there than a line.
  const isBar = mode === MODE_BY_DATE && granularity === GRANULARITY_MONTH;

  return {
    chart: {
      type: isBar ? 'bar' : 'line',
      height: '100%',
      fontFamily: 'inherit',
      animations: { enabled: false },
      zoom: {
        enabled: !isBar,
        type: 'x',
        autoScaleYaxis: true,
        // Otherwise the chart swallows page scrolling.
        allowMouseWheelZoom: false,
      },
      toolbar: {
        show: true,
        export: {
          csv: {
            headerCategory: 'Дата',
            categoryFormatter: (timestamp) =>
              new Date(Number(timestamp)).toISOString().slice(0, 10),
          },
        },
      },
    },
    colors: SERIES_COLORS,
    stroke: {
      curve: granularity === GRANULARITY_DAY ? 'straight' : 'smooth',
      width: 2,
    },
    markers: { size: 0, hover: { size: 4 } },
    dataLabels: { enabled: false },
    legend: { show: true, position: 'top', horizontalAlign: 'left' },
    grid: { borderColor: '#E9ECF5', strokeDashArray: 4 },
    noData: { text: 'Нет данных за выбранный период' },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: true,
        rotate: -45,
        rotateAlways: granularity === GRANULARITY_DAY,
        formatter: axisFormatter(mode, granularity),
      },
      tooltip: { enabled: false },
      axisBorder: { show: false },
    },
    yaxis: {
      min: 0,
      title: { text: cumulative ? 'Заявок нарастающим итогом' : 'Заявок' },
      labels: {
        formatter: (value) =>
          value === null || value === undefined
            ? ''
            : Math.round(value).toLocaleString('ru-RU'),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: true,
      x: { formatter: tooltipFormatter(mode, granularity) },
      y: {
        formatter: (value) =>
          value === null || value === undefined
            ? '—'
            : value.toLocaleString('ru-RU'),
      },
    },
  };
}
